import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Lock, KeyRound, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1512100356356-de1b84283e18?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwyfHxwcml2YXRlJTIwdHJhdmVsJTIwY2x1YnxlbnwwfHx8fDE3ODM2NDMyMjF8MA&ixlib=rb-4.1.0&q=85";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();

  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const verify = async () => {
      if (!token) { setValid(false); setChecking(false); return; }
      try {
        const { data } = await api.post("/auth/verify-reset-token", { token });
        setValid(!!data.valid);
      } catch {
        setValid(false);
      } finally {
        setChecking(false);
      }
    };
    verify();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password reset successfully");
      setTimeout(() => navigate("/login"), 2200);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[var(--ir-bg)]" data-testid="reset-password-page">
      <div className="hidden lg:block relative">
        <img src={HERO} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--ir-blue-dark)]/70 via-[var(--ir-blue-dark)]/20 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <h2 className="font-display text-4xl leading-tight text-white">Set a new password<br /><span className="text-gradient-orange italic">and back to the club.</span></h2>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-full max-w-md">
          <Link to="/"><Logo size="md" /></Link>

          {checking ? (
            <div className="mt-12 flex items-center gap-3 text-[var(--ir-muted)]" data-testid="reset-checking">
              <Loader2 className="w-5 h-5 animate-spin" /> Verifying your reset link…
            </div>
          ) : !valid ? (
            <div className="mt-10" data-testid="reset-invalid">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-8 h-8 text-[var(--ir-orange)]" />
                <h1 className="font-display text-3xl text-[var(--ir-text)]">Link expired</h1>
              </div>
              <p className="text-[var(--ir-muted)] mt-4 leading-relaxed">This password reset link is invalid or has expired. Reset links are valid for 60 minutes and can only be used once.</p>
              <Button asChild className="mt-8 bg-[var(--ir-orange)] hover:bg-[var(--ir-orange-soft)] text-white font-semibold rounded-none h-12 px-8" data-testid="reset-request-new-btn">
                <Link to="/forgot-password">Request a new link</Link>
              </Button>
            </div>
          ) : done ? (
            <div className="mt-10" data-testid="reset-success">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-8 h-8 text-[var(--ir-orange)]" />
                <h1 className="font-display text-3xl text-[var(--ir-text)]">Password reset</h1>
              </div>
              <p className="text-[var(--ir-muted)] mt-4 leading-relaxed">Your password has been updated. Redirecting you to the login page…</p>
              <Button asChild className="mt-8 bg-[var(--ir-orange)] hover:bg-[var(--ir-orange-soft)] text-white font-semibold rounded-none h-12 px-8" data-testid="reset-to-login-btn">
                <Link to="/login">Go to login</Link>
              </Button>
            </div>
          ) : (
            <>
              <h1 className="font-display text-4xl mt-10 text-[var(--ir-text)]">Choose a new password</h1>
              <p className="text-[var(--ir-muted)] mt-2">Enter a new password for your account.</p>

              <form onSubmit={submit} className="mt-8 space-y-5">
                <div>
                  <Label htmlFor="password" className="text-[var(--ir-text)]">New password</Label>
                  <Input id="password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" className="mt-2 bg-white border-[var(--ir-border)] rounded-none h-12 focus-visible:ring-[var(--ir-blue)]" data-testid="reset-password-input" />
                </div>
                <div>
                  <Label htmlFor="confirm" className="text-[var(--ir-text)]">Confirm password</Label>
                  <Input id="confirm" type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter password" className="mt-2 bg-white border-[var(--ir-border)] rounded-none h-12 focus-visible:ring-[var(--ir-blue)]" data-testid="reset-confirm-input" />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[var(--ir-orange)] hover:bg-[var(--ir-orange-soft)] text-white font-semibold rounded-none h-12" data-testid="reset-submit-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><KeyRound className="w-4 h-4 mr-2" />Reset password</>}
                </Button>
              </form>

              <p className="text-[var(--ir-muted)] mt-8 text-sm">
                <Link to="/login" className="text-[var(--ir-blue)] font-medium hover:underline flex items-center gap-1"><Lock className="w-3.5 h-3.5" />Back to login</Link>
              </p>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default ResetPassword;
